'use client';
import React, { Suspense } from 'react';
import type { LearnResponsePage } from '@benzinga/content-manager';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { MoneyBlocks } from '../MoneyBlocks';
import { LearnAuthorCard, LearnLessonNav, LearnPresentedBy, LearnSyllabus, LearnTopicNews } from '../Learn';
import { MoneyTemplateProps } from '../../entities';
import styles from '../Learn/Learn.module.css';
import { PageCampaigns } from '../Campaign/PageCampaigns';
import { NoFirstRender } from '@benzinga/hooks';

const LayoutAdmin = React.lazy(() => import('@benzinga/blocks').then(module => ({ default: module.LayoutAdmin })));

const LEARN_LOGO = 'https://cdnwp-s3.benzinga.com/wp-content/uploads/2026/09/14180336/learn-logo.png';

export type LearnTemplateProps = Omit<MoneyTemplateProps, 'post'> & { post?: LearnResponsePage | null };

export const LearnTemplate: React.FC<LearnTemplateProps> = ({ news, post }) => {
  const syllabus = post?.cornerstone ?? post?.parent ?? null;

  return (
    <div className={`${styles.page} learn-template relative min-h-screen text-bzblue-300`}>
      <div className={`${styles.coverBand} pointer-events-none absolute`} />
      <div
        className={`${styles.layout} relative mx-auto grid max-w-[1400px] gap-4 px-4 pb-12 pt-6 lg:gap-6 lg:px-8 lg:pb-16 lg:pt-8`}
      >
        <a aria-label="Back to Benzinga Learn" className="block lg:hidden" href="/learn">
          <img alt="Benzinga Learn" className="h-7 w-auto" src={LEARN_LOGO} />
        </a>
        <h1
          className={`${styles.title} pb-5 pt-1 text-[2.125rem] font-bold leading-[1.15] text-white lg:flex lg:h-[350px] lg:items-end lg:self-start lg:p-12 lg:text-[3.25rem] lg:text-[#101010]`}
        >
          {post?.title}
        </h1>
        <aside className={`${styles.aside} contents lg:flex lg:min-w-0 lg:flex-col lg:gap-10`}>
          <div className="contents lg:block lg:min-h-0 lg:flex-auto">
            <div className={'contents lg:block'}>
              <LearnPresentedBy align="center" className="lg:mb-5" logo={post?.presented_by} />
              {post && syllabus && <LearnSyllabus post={post} syllabus={syllabus} />}
              <LearnTopicNews news={news} topic={post?.topic} />
            </div>
          </div>
          {post && <LearnAuthorCard post={post} />}
        </aside>
        <main className={`${styles.main} lg:block lg:min-w-0`}>
          <article className="overflow-hidden rounded-xl bg-white text-[#101010]">
            <div
              className={`${styles.hero} hidden bg-bzblue-300 bg-cover bg-center bg-no-repeat lg:relative lg:block lg:h-[350px]`}
              style={post?.image ? { backgroundImage: `url(${post.image})` } : undefined}
            >
              <a aria-label="Back to Benzinga Learn" className="absolute left-10 top-10 z-10" href="/learn">
                <img alt="Benzinga Learn" className="h-9 w-auto" src={LEARN_LOGO} />
              </a>
            </div>
              <NoFirstRender>
                <React.Suspense>{post && <LayoutAdmin post={post} showClearCache />}</React.Suspense>
              </NoFirstRender>
            {post?.intro && (
              <div className="px-6 pb-6 pt-4 md:px-10 md:pb-8 md:pt-5">
                <div
                  className={`${styles.intro} text-lg leading-relaxed text-bzgrey-500`}
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(post.intro) }}
                />
              </div>
            )}
            <div className="border-t border-bzblue-300 bg-bzblue-200 p-6 md:px-10 md:pb-10 md:pt-8">
              <MoneyBlocks blocks={post?.blocks} campaigns={post?.campaigns} post={post} />
            </div>
          </article>
          {syllabus && <LearnLessonNav syllabus={syllabus} />}
          {Array.isArray(post?.footer?.blocks) && (
            <div className="mt-6 rounded-xl bg-white px-6 py-5 text-[#101010]">
              <MoneyBlocks blocks={post?.footer?.blocks} post={post} />
            </div>
          )}
          {Array.isArray(post?.global_footer?.blocks) && (
            <div className="mt-6 rounded-xl bg-white px-6 py-5 text-[#101010]">
              <MoneyBlocks blocks={post?.global_footer?.blocks} post={post} />
            </div>
          )}
          <Suspense fallback={<div />}>
            <PageCampaigns post={post} />
          </Suspense>
        </main>
      </div>
    </div>
  );
};

export default LearnTemplate;
